import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Container,
  Grid,
  Header,
  SegmentedControl,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import EventsVersionSelector from "./EventsVersionSelector";
import { OrderIdContext, StageContext } from "../../contexes/OrderIdContext";
import { useMarketplaceId } from "../../contexes/MarketplaceIdContext";
import useFetchDetails from "../../hooks/useFetchDetails";
import {
  getCbeLogGroups,
  getEventDateRange,
  getEventLogGroups,
} from "../../helpers/logUtils";
import {
  getAllProductIds,
  isBundleNonSplitTransaction,
  isDocumentBundleTransaction,
} from "../../helpers/bundleUtils";
import CbeVersionSelector from "../cbe/CbeVersionSelector";
import EventButtons from "../common/EventButtons";
import ErrorAlert from "../common/ErrorAlert";
import { useGlProduct } from "../../contexes/GlProductContext";
import { useFlowType } from "../../hooks/useFlowType";
import {
  FLOW_TYPES,
  FlowType,
  getFlowSelectorLabel,
} from "../../helpers/flowTypeUtils";
import {
  isAccountingEventsGroup,
  isCanonicalBusinessEvent,
  isDigitalBusinessDocument,
  isDigitalDocument,
} from "../../helpers/typeChecks";
import { CanonicalBusinessEvent } from "../../types/cbes";
import { AccountingEventsGroup } from "../../types/events";
import {
  DigitalBusinessDocument,
  DigitalCostDocument,
  DigitalPaymentDocument,
} from "../../types/documents";
import { MatchKeyType } from "../../types/matchSetState";
import { Stage } from "../../lusca-search-api/generated-src";
import { JsonModal } from "../common/JsonModal";
import { ModalSubtitle } from "../common/ModalSubtitle";
import { cbeWithoutPublishStatus } from "../../helpers/cbeSummaryUtils";
import { clearModalParam, setModalParam } from "../../helpers/modalUrlUtils";

interface CbePanelProps {
  orderId: string;
  stage: Stage;
  cbeLogGroups: string[];
  filteredCbes: CanonicalBusinessEvent[];
  cbeData: CanonicalBusinessEvent[];
  selectedCbeIndex: number;
  isCompareCbesOpen: boolean;
  openCompareModal: () => void;
  closeCompareModal: () => void;
  productsToggleConfig: { show: boolean; onToggle: () => void } | null;
  versionViewConfig: {
    show: boolean;
    isTopLevel: boolean;
    onToggle: () => void;
  } | null;
  handleCbeVersionChange: (index: number) => void;
  allProductIds: string[];
  isDocumentBundle: boolean;
  handleProductsToggle: (toggleFunction: () => void) => void;
  handleVersionViewToggle: (
    toggleFunction: () => void,
    isTopLevel: boolean,
    shouldShow: boolean
  ) => void;
  initialCbeIndex: number | undefined;
  isCbeNotEmpty: boolean;
  showFlowTypeSelector: boolean;
  flowType: FlowType;
}

const CbePanel = React.memo(function CbePanel({
  orderId,
  stage,
  cbeLogGroups,
  filteredCbes,
  cbeData,
  selectedCbeIndex,
  isCompareCbesOpen,
  openCompareModal,
  closeCompareModal,
  productsToggleConfig,
  versionViewConfig,
  handleCbeVersionChange,
  allProductIds,
  isDocumentBundle,
  handleProductsToggle,
  handleVersionViewToggle,
  initialCbeIndex,
  isCbeNotEmpty,
  showFlowTypeSelector,
  flowType,
}: CbePanelProps) {
  return (
    <Container
      header={
        <Header
          variant="h2"
          actions={
            <EventButtons
              identifier={orderId}
              stage={stage}
              logGroups={cbeLogGroups}
              dateRange={getEventDateRange(
                filteredCbes[selectedCbeIndex] || cbeData[selectedCbeIndex],
                true
              )}
              data={filteredCbes}
              allCbes={cbeData}
              allProductIds={allProductIds}
              modalControl={{
                visible: isCompareCbesOpen,
                onOpen: openCompareModal,
                onClose: closeCompareModal,
              }}
              collapseProductsConfig={productsToggleConfig || undefined}
              versionViewConfig={versionViewConfig || undefined}
            />
          }
        >
          CBEs
        </Header>
      }
    >
      {isCbeNotEmpty && filteredCbes.length > 0 ? (
        <CbeVersionSelector
          cbeData={filteredCbes}
          onVersionChange={handleCbeVersionChange}
          allProductIds={allProductIds}
          showCbeProductBreakdown={isDocumentBundle}
          onProductsToggle={handleProductsToggle}
          onVersionViewToggle={handleVersionViewToggle}
          initialIndex={initialCbeIndex}
        />
      ) : isCbeNotEmpty ? (
        <ErrorAlert
          header={
            showFlowTypeSelector
              ? `No ${
                  flowType === FLOW_TYPES.DOCUMENT
                    ? MatchKeyType.ROYALTY
                    : MatchKeyType.REVENUE
                } CBEs found`
              : "No CBEs found"
          }
          content={
            showFlowTypeSelector
              ? `Try changing the flow type filter to view ${
                  flowType === FLOW_TYPES.DOCUMENT
                    ? MatchKeyType.REVENUE
                    : MatchKeyType.ROYALTY
                } CBEs.`
              : ""
          }
        />
      ) : (
        <ErrorAlert header="Could not find any CBEs" content="" />
      )}
    </Container>
  );
});

const getVersion = (
  data:
    | CanonicalBusinessEvent
    | AccountingEventsGroup
    | DigitalBusinessDocument
    | DigitalCostDocument
    | DigitalPaymentDocument
): number => {
  if (isCanonicalBusinessEvent(data)) {
    return data.lockVersion;
  }

  if (isDigitalDocument(data)) {
    return data.version;
  }

  if (isAccountingEventsGroup(data)) {
    return data.sk;
  }

  throw Error("unsupported getVersion data", data);
};

export default function Results() {
  const orderId = useContext(OrderIdContext);
  const stage = useContext(StageContext);
  const { marketplaceId } = useMarketplaceId();
  const { glProduct } = useGlProduct();
  // All URL writes use window.history.replaceState to avoid React Router re-render cascades.
  // Modal visibility is driven by local state; URL is kept in sync for deep linking.

  const [productsToggleConfig, setProductsToggleConfig] = useState<{
    show: boolean;
    onToggle: () => void;
  } | null>(null);

  const [compareModal, setCompareModal] = useState<string | null>(() => {
    const modal = new URLSearchParams(window.location.search).get("modal");
    return modal === "compare-events" || modal === "compare-cbes"
      ? modal
      : null;
  });
  const isCompareEventsOpen = compareModal === "compare-events";
  const isCompareCbesOpen = compareModal === "compare-cbes";

  const [cbeJsonModal, setCbeJsonModal] = useState<{
    cbeId: string;
    cbeV: string;
  } | null>(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("modal") === "cbe-json") {
      const cbeId = params.get("cbeId");
      const cbeV = params.get("cbeV");
      if (cbeId && cbeV) return { cbeId, cbeV };
    }
    return null;
  });

  const openCompareModal = useCallback((id: string) => {
    setCompareModal(id);
    setModalParam(id);
  }, []);

  const closeCompareModal = useCallback(() => {
    setCompareModal(null);
    clearModalParam();
  }, []);

  const openCompareCbes = useCallback(
    () => openCompareModal("compare-cbes"),
    [openCompareModal]
  );
  const openCompareEvents = useCallback(
    () => openCompareModal("compare-events"),
    [openCompareModal]
  );

  const [versionViewConfig, setVersionViewConfig] = useState<{
    show: boolean;
    isTopLevel: boolean;
    onToggle: () => void;
  } | null>(null);

  const { data: detailsData } = useFetchDetails(stage, orderId);

  const eventData = detailsData?.events || [];
  const cbeData = detailsData?.cbes || [];

  const isEventNotEmpty = eventData.length > 0;
  const isCbeNotEmpty = cbeData.length > 0;

  const handleProductsToggle = useCallback((toggleFunction: () => void) => {
    setProductsToggleConfig({
      show: true,
      onToggle: toggleFunction,
    });
  }, []);

  const handleVersionViewToggle = useCallback(
    (toggleFunction: () => void, isTopLevel: boolean, shouldShow: boolean) => {
      setVersionViewConfig({
        show: shouldShow,
        isTopLevel,
        onToggle: toggleFunction,
      });
    },
    []
  );

  const {
    flowType,
    setFlowType,
    showFlowTypeSelector,
    filteredEvents,
    filteredCbes,
  } = useFlowType({
    eventData,
    cbeData,
    glProduct,
  });

  const { allProductIds, isDocumentBundle } = useMemo(() => {
    const documents = filteredEvents.filter(isDigitalBusinessDocument);

    if (documents.length === 0) {
      return { allProductIds: [], isDocumentBundle: false };
    }

    const isDocumentBundleDetected = isDocumentBundleTransaction(documents[0]);
    const productIds = Array.from(getAllProductIds(documents));
    const isBundleNonSplitDetected = isBundleNonSplitTransaction(documents);
    const hasMultipleProducts = productIds.length > 1;

    return {
      allProductIds: productIds,
      isDocumentBundle:
        hasMultipleProducts ||
        (isDocumentBundleDetected && !isBundleNonSplitDetected),
    };
  }, [filteredEvents]);

  const initialEventIndex = useMemo(() => {
    const urlVersion = new URLSearchParams(window.location.search).get(
      "eventVersion"
    );
    if (urlVersion !== null && filteredEvents.length > 0) {
      const parsed = parseInt(urlVersion, 10);
      if (!isNaN(parsed)) {
        const index = filteredEvents.findIndex((e) => getVersion(e) === parsed);
        if (index !== -1) return index;
      }
    }
    return undefined;
  }, [filteredEvents]);

  const initialCbeIndex = useMemo(() => {
    const urlVersion = new URLSearchParams(window.location.search).get(
      "cbeVersion"
    );
    if (urlVersion !== null && filteredCbes.length > 0) {
      const parsed = parseInt(urlVersion, 10);
      if (!isNaN(parsed)) {
        const index = filteredCbes.findIndex((c) => c.lockVersion === parsed);
        if (index !== -1) return index;
      }
    }
    return undefined;
  }, [filteredCbes]);

  const [selectedEventIndex, setSelectedEventIndex] = useState<number>(0);

  const [selectedCbeIndex, setSelectedCbeIndex] = useState<number>(0);

  const handleEventVersionChange = useCallback(
    (index: number) => {
      setSelectedEventIndex(index);
      const version = getVersion(filteredEvents[index]);
      const url = new URL(window.location.href);
      const highestVersion =
        filteredEvents.length > 0
          ? Math.max(...filteredEvents.map((e) => getVersion(e)))
          : undefined;
      const modalParam = url.searchParams.get("modal");
      if (version === highestVersion && modalParam !== "event-json") {
        url.searchParams.delete("eventVersion");
      } else {
        url.searchParams.set("eventVersion", version.toString());
      }
      window.history.replaceState(null, "", url.toString());
    },
    [filteredEvents]
  );

  const handleCbeVersionChange = useCallback(
    (index: number) => {
      setSelectedCbeIndex(index);
      const version = filteredCbes[index].lockVersion;
      const url = new URL(window.location.href);
      const highestVersion =
        filteredCbes.length > 0
          ? Math.max(...filteredCbes.map((c) => c.lockVersion))
          : undefined;
      if (version === highestVersion) {
        url.searchParams.delete("cbeVersion");
      } else {
        url.searchParams.set("cbeVersion", version.toString());
      }
      window.history.replaceState(null, "", url.toString());
    },
    [filteredCbes]
  );

  const eventLogGroups = useMemo(() => {
    if (!isEventNotEmpty) return [];

    const dateRange = getEventDateRange(
      filteredEvents[selectedEventIndex] || eventData[selectedEventIndex],
      false
    );
    const startTime = dateRange[0];

    return getEventLogGroups(stage, filteredEvents, flowType, startTime);
  }, [
    isEventNotEmpty,
    stage,
    filteredEvents,
    flowType,
    selectedEventIndex,
    eventData,
  ]);

  const cbeLogGroups = useMemo(() => {
    return getCbeLogGroups(marketplaceId, stage);
  }, [marketplaceId, stage]);

  return (
    <Grid
      gridDefinition={[
        { colspan: { default: 12, s: 6 } },
        { colspan: { default: 12, s: 6 } },
      ]}
    >
      <Container
        header={
          <Header
            variant="h2"
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <EventButtons
                  identifier={orderId}
                  stage={stage}
                  logGroups={eventLogGroups}
                  dateRange={getEventDateRange(
                    filteredEvents[selectedEventIndex] ||
                      eventData[selectedEventIndex],
                    false
                  )}
                  data={filteredEvents}
                  modalControl={{
                    visible: isCompareEventsOpen,
                    onOpen: openCompareEvents,
                    onClose: closeCompareModal,
                  }}
                />

                {showFlowTypeSelector && (
                  <SegmentedControl
                    selectedId={flowType}
                    onChange={({ detail }) => {
                      setFlowType(detail.selectedId as FlowType);
                      const url = new URL(window.location.href);
                      url.searchParams.delete("cbeVersion");
                      url.searchParams.delete("eventVersion");
                      window.history.replaceState(null, "", url.toString());
                    }}
                    options={[
                      {
                        text: getFlowSelectorLabel(
                          FLOW_TYPES.ACCOUNTING,
                          eventData,
                          cbeData
                        ),
                        id: FLOW_TYPES.ACCOUNTING,
                      },
                      {
                        text: getFlowSelectorLabel(
                          FLOW_TYPES.DOCUMENT,
                          eventData,
                          cbeData
                        ),
                        id: FLOW_TYPES.DOCUMENT,
                      },
                    ]}
                  />
                )}
              </SpaceBetween>
            }
          >
            Events
          </Header>
        }
      >
        {isEventNotEmpty && filteredEvents.length > 0 ? (
          <EventsVersionSelector
            eventData={filteredEvents}
            cbeData={cbeData}
            onVersionChange={handleEventVersionChange}
            initialIndex={initialEventIndex}
          />
        ) : isEventNotEmpty ? (
          <ErrorAlert
            header={
              showFlowTypeSelector
                ? `No ${
                    flowType === FLOW_TYPES.DOCUMENT
                      ? MatchKeyType.ROYALTY
                      : MatchKeyType.REVENUE
                  } events found`
                : "No events found"
            }
            content={
              showFlowTypeSelector
                ? `Try changing the flow type filter to view ${
                    flowType === FLOW_TYPES.DOCUMENT
                      ? MatchKeyType.REVENUE
                      : MatchKeyType.ROYALTY
                  } events.`
                : ""
            }
          />
        ) : (
          <ErrorAlert header="Could not find any events" content="" />
        )}
      </Container>
      <CbePanel
        orderId={orderId}
        stage={stage}
        cbeLogGroups={cbeLogGroups}
        filteredCbes={filteredCbes}
        cbeData={cbeData}
        selectedCbeIndex={selectedCbeIndex}
        isCompareCbesOpen={isCompareCbesOpen}
        openCompareModal={openCompareCbes}
        closeCompareModal={closeCompareModal}
        productsToggleConfig={productsToggleConfig}
        versionViewConfig={versionViewConfig}
        handleCbeVersionChange={handleCbeVersionChange}
        allProductIds={allProductIds}
        isDocumentBundle={isDocumentBundle}
        handleProductsToggle={handleProductsToggle}
        handleVersionViewToggle={handleVersionViewToggle}
        initialCbeIndex={initialCbeIndex}
        isCbeNotEmpty={isCbeNotEmpty}
        showFlowTypeSelector={showFlowTypeSelector}
        flowType={flowType}
      />
      {(() => {
        if (!cbeJsonModal) return null;
        const cbe = cbeData.find(
          (c) =>
            c.idempotenceId === cbeJsonModal.cbeId &&
            String(c.idempotenceVersion) === cbeJsonModal.cbeV
        );
        if (!cbe) return null;
        return (
          <JsonModal
            title={
              <>
                CBE · V{cbe.idempotenceVersion} · {cbe.destination}
                <ModalSubtitle text={cbe.idempotenceId} />
              </>
            }
            content={cbeWithoutPublishStatus(cbe)}
            visible
            onDismiss={() => {
              setCbeJsonModal(null);
              clearModalParam();
            }}
            initialCollapsed={2}
          />
        );
      })()}
    </Grid>
  );
}
